import type { StudentPendingSurveysQuery } from 'core/graphql';

/** Plain-String contract values of `questionType` (spec 0011 §4). */
export type SurveyQuestionKind =
  | 'yes_no'
  | 'text'
  | 'rating'
  | 'single_choice'
  | 'multi_choice'
  | 'nps'
  // Teacher-rating extension (spec 0011): plain-String aliases of `rating` /
  // `text` on the wire — no schema change. Rendered/validated identically.
  | 'teacher_rating'
  | 'teacher_comment';

export interface ISurveyQuestion {
  id: string;
  number: number;
  text: string;
  questionType: SurveyQuestionKind;
  required: boolean;
  /** Variant labels; non-empty only for single_choice / multi_choice. */
  options: string[];
}

export interface IPendingSurvey {
  assignmentId: string;
  title: string;
  description: string;
  groupName: string;
  lessonName: string;
  lessonNumber: number;
  /** Who the teacher_rating / teacher_comment questions are about; '' if none. */
  teacherName: string;
  questions: ISurveyQuestion[];
}

/**
 * Normalizes the `mySurveys` payload into view models. `questionType` arrives
 * as a plain String — narrow it to the known union; an unknown value (future
 * question type from a newer backend) degrades to 'text' so the form still
 * renders something answerable instead of crashing.
 */
export const transformPendingSurveys = (
  data: StudentPendingSurveysQuery | undefined,
): IPendingSurvey[] =>
  (data?.mySurveys ?? []).map((survey) => ({
    assignmentId: survey.assignmentId,
    title: survey.title,
    description: survey.description,
    groupName: survey.groupName,
    teacherName: survey.teacherName || '',
    lessonName: survey.lessonName,
    lessonNumber: survey.lessonNumber,
    questions: survey.questions.map((question) => ({
      id: question.id,
      number: question.number,
      text: question.text,
      questionType: isKnownQuestionKind(question.questionType) ? question.questionType : 'text',
      required: question.required,
      options: question.options ?? [],
    })),
  }));

const KNOWN_KINDS: SurveyQuestionKind[] = [
  'yes_no',
  'text',
  'rating',
  'single_choice',
  'multi_choice',
  'nps',
  'teacher_rating',
  'teacher_comment',
];

const isKnownQuestionKind = (value: string): value is SurveyQuestionKind =>
  (KNOWN_KINDS as string[]).includes(value);
